import { mutateCollectionLocation } from '../firebase/functions';
import { inventoryMutations } from '../services/inventoryMutationCoordinator';
import {
  addDoc,
  collection,
  getDocs,
  query,
  serverTimestamp,
  where,
} from 'firebase/firestore';
import { InventoryAuthority, inventoryFirestorePath } from '@qkindex/contracts';
import { db } from '../firebase';
import { normalizeLocationName } from '../domain/location';

export interface LocationRepository {
  create(
    authority: InventoryAuthority,
    name: string,
    description: string,
  ): Promise<string>;
  update(
    authority: InventoryAuthority,
    locationId: string,
    values: { name: string; description: string },
  ): Promise<void>;
  delete(authority: InventoryAuthority, locationId: string): Promise<void>;
  findByNormalizedName(
    authority: InventoryAuthority,
    normalizedName: string,
  ): Promise<string[]>;
  countItems(
    authority: InventoryAuthority,
    locationId: string,
    legacyName: string,
  ): Promise<number>;
}

export const firebaseLocationRepository: LocationRepository = {
  async create(authority, name, description) {
    const created = await addDoc(
      collection(db, inventoryFirestorePath(authority, 'locations')),
      {
        ...(authority.compatibilityMode === 'collection-only'
          ? { collectionId: authority.collectionId }
          : {}),
        name,
        normalizedName: normalizeLocationName(name),
        description,
        createdAt: serverTimestamp(),
        lastEditedAt: serverTimestamp(),
        lastEditedBy: authority.userId,
      },
    );
    return created.id;
  },
  async update(authority, locationId, values) {
    inventoryFirestorePath(authority, 'locations');
    const payload = { ...values };
    await inventoryMutations.run(
      authority.userId,
      [{ collectionId: authority.collectionId }],
      JSON.stringify([
        'location-update',
        authority.collectionId,
        locationId,
        payload,
      ]),
      'Update location',
      async (operationId) => {
        await mutateCollectionLocation({
          action: 'update',
          collectionId: authority.collectionId,
          locationId,
          operationId,
          ...payload,
        });
      },
    );
  },
  async delete(authority, locationId) {
    inventoryFirestorePath(authority, 'locations');
    await inventoryMutations.run(
      authority.userId,
      [{ collectionId: authority.collectionId }],
      JSON.stringify(['location-delete', authority.collectionId, locationId]),
      'Delete location',
      async (operationId) => {
        await mutateCollectionLocation({
          action: 'delete',
          collectionId: authority.collectionId,
          locationId,
          operationId,
        });
      },
    );
  },
  async findByNormalizedName(authority, normalizedName) {
    const snapshot = await getDocs(
      query(
        collection(db, inventoryFirestorePath(authority, 'locations')),
        where('normalizedName', '==', normalizedName),
      ),
    );
    return snapshot.docs.map((snapshot) => snapshot.id);
  },
  async countItems(authority, locationId, legacyName) {
    const items = collection(db, inventoryFirestorePath(authority, 'items'));
    const [byId, byName] = await Promise.all([
      getDocs(query(items, where('locationId', '==', locationId))),
      // Compatibility check until the locationId migration is complete.
      getDocs(query(items, where('location', '==', legacyName))),
    ]);
    return new Set(
      [
        ...byId.docs,
        ...byName.docs.filter((snapshot) => !snapshot.data().locationId),
      ].map((snapshot) => snapshot.id),
    ).size;
  },
};
